import styled from "@emotion/styled";
import React, { memo } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { GraySmallCalendarIcon } from "../assets/icons/icons";
import ko from "date-fns/locale/ko";
import en from "date-fns/locale/en-US";
import zh from "date-fns/locale/zh-CN";
import ja from "date-fns/locale/ja";
import vi from "date-fns/locale/vi";
import es from "date-fns/locale/es";
import { add } from "date-fns";
import theme from "../theme";
import { useWindowSize } from "@reactuses/core";
import { useI18n } from "../locales";
interface CalendarProps {
  header: string;
  date?: Date | null;
  setDate: (param: Date | null) => void;
  width?: number;
  isClearable?: boolean; // 날짜 지우기 버튼
}
const { md } = theme.screen;

registerLocale("ko", ko);
registerLocale("en", en);
registerLocale("zh", zh);
registerLocale("ja", ja);
registerLocale("vi", vi);
registerLocale("es", es);

const Calendar: React.FC<CalendarProps> = memo((props) => {
  const { locale, t } = useI18n();
  const { header, date, setDate, width, isClearable } = props;
  const { width: windowWidth } = useWindowSize();
  
  // DatePicker locale prop은 'ko', 'en' 등의 문자열을 사용
  const datePickerLocale = locale;
  
  // 언어별 날짜 형식 설정
  const getDateFormat = (locale: string) => {
    switch (locale) {
      case 'ko':
        return 'yyyy년 MM월 dd일';
      case 'en':
        return 'MMM dd, yyyy';
      case 'es':
        return 'dd MMM yyyy';
      case 'zh':
        return 'yyyy年MM月dd日';
      case 'ja':
        return 'yyyy年MM月dd日';
      case 'vi':
        return 'dd/MM/yyyy';
      default:
        return 'yyyy-MM-dd';
    }
  };

  // 언어별 월 이름 배열
  const monthNames = {
    ko: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    zh: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
    ja: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
    vi: ['Th1', 'Th2', 'Th3', 'Th4', 'Th5', 'Th6', 'Th7', 'Th8', 'Th9', 'Th10', 'Th11', 'Th12'],
  };

  // 커스텀 헤더 렌더링 함수 - 연도 단위만 추가하고 기존 드롭다운 유지
  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    changeYear,
    changeMonth,
  }: any) => {
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    const yearUnit = t.common.yearUnit;
    
    // 연도 옵션 생성 (2020-2030)
    const years = Array.from({ length: 11 }, (_, i) => 2020 + i);
    const months = Array.from({ length: 12 }, (_, i) => i);
    
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
        <button
          type="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          ←
        </button>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <select
            value={currentYear}
            onChange={({ target: { value } }) => changeYear(parseInt(value))}
            style={{ background: 'transparent', border: 'none', fontWeight: 'bold', fontSize: '17px' }}
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <span style={{ fontWeight: 'bold', fontSize: '17px' }}>{yearUnit}</span>
          
          <select
            value={currentMonth}
            onChange={({ target: { value } }) => changeMonth(parseInt(value))}
            style={{ background: 'transparent', border: 'none', fontWeight: 'bold', fontSize: '17px' }}
          >
            {months.map(month => (
              <option key={month} value={month}>{monthNames[locale as keyof typeof monthNames]?.[month]}</option>
            ))}
          </select>
        </div>
        
        <button
          type="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          →
        </button>
      </div>
    );
  };

  const ExampleCustomInput = React.forwardRef(
    (
      {
        value,
        onClick,
      }: {
        value: string;
        onClick: (
          event: React.MouseEvent<HTMLButtonElement, MouseEvent>
        ) => void;
      },
      _
    ) => (
      <Button
        width={width}
        className="example-custom-input"
        onClick={onClick}
      >
        {value}
      </Button>
    )
  );

  return (
    <Wrapper $windowWidth={windowWidth}>
      <Header>{header}</Header>
      <IconWrapper>
        <GraySmallCalendarIcon />
      </IconWrapper>
      <DatePicker
        dateFormat={getDateFormat(locale)}
        locale={datePickerLocale}
        selected={date}
        onChange={(date) => setDate(date)}
        showYearDropdown
        showMonthDropdown
        customInput={React.createElement(ExampleCustomInput)}
        yearItemNumber={10}
        dropdownMode="select"
        minDate={new Date("2020")}
        maxDate={add(new Date("2020"), { years: 10 })}
        isClearable={isClearable}
        calendarStartDay={locale === 'ko' ? 0 : 1}
        renderCustomHeader={renderCustomHeader}
      />
    </Wrapper>
  );
});

const Wrapper = styled.div<{ $windowWidth: number }>`
  position: relative;
  .react-datepicker-popper {
    z-index: ${theme.zindex.accidentBlink}; // 9999
  }
  .react-datepicker {
    background-color: ${theme.color.neutral_100};
    color: ${theme.color.black};
    border: 0px solid ${theme.color.neutral_300};
    border-radius: 15px;

    &__header {
      background-color: ${theme.color.neutral_100};
      border-bottom: 0px solid ${theme.color.neutral_300};
      border-radius: 15px;
    }
    &__header__dropdown {
      padding-top: 30px;
      margin-left: 5px;
    }

    &__header__dropdown::after {
      top: 9px;
      left: 56px;
    }

    &__year-select {
      top: -3px;
      left: 5px;
      position: absolute;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      padding: 10px 10px 10px 10px;
      cursor: pointer;

      option {
        border: none;
        background-color: ${theme.color.neutral_600};
        color: ${theme.color.neutral_50};
      }
    }

    &__month-select {
      position: absolute;
      top: -3px;
      left: 67px;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      padding: 10px 10px 10px 10px;
      cursor: pointer;

      option {
        border: none;
        background-color: ${theme.color.neutral_600};
        color: ${theme.color.neutral_50};
      }
    }

    &__day {
      &:hover {
        background-color: ${theme.color.neutral_200};
      }
    }

    &__day-name {
      color: ${theme.color.neutral_300};
      font-size: 1em;
    }
    &__input-container {
      /* border: 1.4px solid rgba(0, 0, 0, 0); */
      z-index: 3;
      &:hover {
        border-radius: 3px;
        /* border: 1.4px solid ${theme.color.neutral_400}; */
      }
    }
    &__navigation--previous {
      top: 12px;
      left: 180px;
    }
    &__navigation--next {
      top: 12px;
    }
    &__day--outside-month {
      color: ${theme.color.neutral_300};
    }
    &__day--keyboard-selected,
    &__day--selected {
      border-radius: 0.3rem !important;
      background: ${theme.statusColor.MAIN} !important;
    }
    &__close-icon {
      position: absolute;
      right: 10px !important; /* 위치 조정 */
      top: 50% !important;
      transform: translateY(-50%);
      width: 16px !important;
      height: 16px !important;
      display: flex !important;
      justify-content: center;
      align-items: center;
      padding: 0px;
    }
    &__close-icon::after {
      width: 16px;
      height: 16px;
      font-size: 16px;
      line-height: 16px;
      background-color: ${theme.color.neutral_300};
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0px;
    }
  }
  .react-datepicker-popper[data-placement^="bottom"]
    .react-datepicker__triangle::before {
    border-bottom-color: rgba(0, 0, 0, 0);
  }
`;

const Header = styled.strong`
  display: block;
  font-weight: 400;
  font-size: 11px;
  line-height: 13px;
  color: ${theme.color.neutral_500};
  margin-bottom: 6px;

  @media (max-width: ${md}px) {
    font-size: ${theme.fontSize.FONT_3};
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 32px;
  z-index: 4;

  @media (max-width: ${md}px) {
    top: 22px;

    svg {
      width: 10.9px;
      height: 10px;
    }
  }
`;

const Button = styled.button<{ width?: number }>`
  width: ${({ width }) => (width ? width : 175)}px;
  height: 40px;
  background: ${theme.color.neutral_100};
  border-radius: 3px;
  border: none;
  font-weight: 400;
  font-size: ${theme.fontSize.FONT_1};
  line-height: 16px;
  color: ${theme.color.neutral_500};
  cursor: pointer;

  @media (max-width: ${md}px) {
    height: 24px;
    font-size: ${theme.fontSize.FONT_2};
  }
`;

export default Calendar;
